// mds-dojo — dijit/_AttachMixin — FAITHFUL port of references/dijit/_AttachMixin.js.
// Codemod pipeline; finished: declare from the faithful runtime; fixed the transform's
// first-`return` mis-rewrite (getAttrFunc returns the attribute; module export
// defaults _AttachMixin); `require("./a11yclick")` for ondijitclick → mapped to
// "click" (evergreen-accessible + matches the app's proven behavior — see
// BIGAPP-GOTCHAS ondijitclick→click). Promoted to canonical.
// Wires data-dojo-attach-point / data-dojo-attach-event on template nodes.
import array from '../dojo/_base/array';
import connect from '../dojo/_base/connect';
import declare from '../dojo/_base/declare-runtime';
import lang from '../dojo/_base/lang';
import mouse from '../dojo/mouse';
import on from '../dojo/on';
import touch from '../dojo/touch';
import _WidgetBase from './_WidgetBase';

// Map from string name like "mouseenter" to synthetic event like mouse.enter
const synthEvents: any = lang.delegate(touch, {
  mouseenter: mouse.enter,
  mouseleave: mouse.leave,
  keypress: (connect as any)._keypress, // remove for 2.0
});

const _AttachMixin = declare('dijit._AttachMixin', null, {
  constructor: function () {
    this._attachPoints = [];
    this._attachEvents = [];
  },

  buildRendering: function () {
    this.inherited(arguments);
    // recurse through the node, attaching to our attach points and events.
    this._attachTemplateNodes(this.domNode);
    this._beforeFillContent(); // hook for _WidgetsInTemplateMixin
  },

  _beforeFillContent: function () {
  },

  _attachTemplateNodes: function (rootNode: any) {
    // DFS to process all nodes except those inside this.containerNode.
    var node = rootNode;
    while (true) {
      if (node.nodeType == 1 && (this._processTemplateNode(node, function (n: any, p: string) { return n.getAttribute(p); },
        this._attach) || this.searchContainerNode) && node.firstChild) {
        node = node.firstChild;
      } else {
        if (node == rootNode) { return; }
        while (!node.nextSibling) {
          node = node.parentNode;
          if (node == rootNode) { return; }
        }
        node = node.nextSibling;
      }
    }
  },

  _processTemplateNode: function (baseNode: any, getAttrFunc: any, attachFunc: any) {
    // Process data-dojo-attach-point / -event. Returns true if children should be scanned.
    var ret = true;

    var _attachScope = this.attachScope || this,
      attachPoint = getAttrFunc(baseNode, 'dojoAttachPoint') || getAttrFunc(baseNode, 'data-dojo-attach-point');
    if (attachPoint) {
      var point, points = attachPoint.split(/\s*,\s*/);
      while ((point = points.shift())) {
        if (lang.isArray(_attachScope[point])) {
          _attachScope[point].push(baseNode);
        } else {
          _attachScope[point] = baseNode;
        }
        ret = (point != 'containerNode');
        this._attachPoints.push(point);
      }
    }

    var attachEvent = getAttrFunc(baseNode, 'dojoAttachEvent') || getAttrFunc(baseNode, 'data-dojo-attach-event');
    if (attachEvent) {
      // supports "domEvent: nativeEvent, ..."
      var event, events = attachEvent.split(/\s*,\s*/);
      var trim = lang.trim;
      while ((event = events.shift())) {
        if (event) {
          var thisFunc = null;
          if (event.indexOf(':') != -1) {
            var funcNameArr = event.split(':');
            event = trim(funcNameArr[0]);
            thisFunc = trim(funcNameArr[1]);
          } else {
            event = trim(event);
          }
          if (!thisFunc) {
            thisFunc = event;
          }
          this._attachEvents.push(attachFunc.call(this, baseNode, event, lang.hitch(_attachScope, thisFunc)));
        }
      }
    }

    return ret;
  },

  _attach: function (node: any, type: string, func: any) {
    // Default processor for a data-dojo-attach-event. Maps special type names to
    // synthetic events. dijitclick → "click" (accessible-click; see header note).
    type = type.replace(/^on/, '').toLowerCase();
    if (type == 'dijitclick') {
      type = 'click' as any;
    } else {
      type = synthEvents[type] || type;
    }
    return on(node, type, func);
  },

  _detachTemplateNodes: function () {
    // Detach + clean up attachments made in _attachTemplateNodes.
    var _attachScope = this.attachScope || this;
    array.forEach(this._attachPoints, function (point: string) {
      delete _attachScope[point];
    });
    this._attachPoints = [];

    array.forEach(this._attachEvents, function (handle: any) { handle.remove(); });
    this._attachEvents = [];
  },

  destroyRendering: function () {
    this._detachTemplateNodes();
    this.inherited(arguments);
  },
});

// Widgets used in templates can carry these attrs. Mix into the base widget class.
lang.extend(_WidgetBase, {
  dojoAttachEvent: '',
  dojoAttachPoint: '',
});

export default _AttachMixin;
